import { FC, useCallback, useState } from 'react'
import Button from '@UI/New/Button'
import {
  LoadingOutlined,
  PlusCircleFilled,
  ReloadOutlined,
} from '@ant-design/icons'
import SetCourseClassDirectionModal from './components/SetCourseClassDirectionModal'
import CourseClassList from './components/CourseClassList/CourseClassList'
import { Empty } from 'antd'
import { useLeadRefetch } from '../../LeadRefetchContext'
import { useAsyncRetry } from 'react-use'
import { crmApi } from '@/api'
import { useLeadStatuses } from '../../blocks/StatusListBlock/LeadStatusContext'
import classes from './index.module.css'
import useFullLeadModalAccess from '../../hooks/useFullLeadModalAccess'
import type { LeadProfileLead } from '../../LeadProfileModal.types'

interface PurchasesBlockProps {
  lead: LeadProfileLead
  currentLeadFunnelId?: number
  excludedClassIds: number[]
  excludedDirectionIds: number[]
  refetchExcludedIds: () => void
  onKanbanCardSync?: () => void
}

const PurchasesBlock: FC<PurchasesBlockProps> = ({
  lead,
  currentLeadFunnelId,
  excludedClassIds,
  excludedDirectionIds,
  refetchExcludedIds,
  onKanbanCardSync,
}) => {
  const hasFunnel = !!currentLeadFunnelId && currentLeadFunnelId > 0
  const hasLinkedAccount =
    lead.user_id != null && Number(lead.user_id) > 0
  /** Без user_id список не грузим; «Добавить» — достаточно выбранной воронки. */

  const [stateModal, setStateModal] = useState<{
    open: boolean
    type?: 'add'
  }>({
    open: false,
    type: 'add',
  })
  const { refreshStatuses } = useLeadStatuses()
  const {
    handleRefetchLeadInModal,
    handleRefetchLeadsOnClose,
    isRefetchingLeadCard,
    selectedFunnelId,
    initialFunnelIdForRefetch,
  } = useLeadRefetch()

  const { currentRole, ...access } = useFullLeadModalAccess()

  // prettier-ignore
  const paymentState = useAsyncRetry(
    async () =>
      lead?.user_id
        ? await crmApi.getLeadPayments({
            user_id: lead.user_id,
          })
        : null,
    [lead?.user_id],
  )
  const paymentData = {
    classes: paymentState.value?.data?.data?.classes || [],
    directions: paymentState.value?.data?.data?.directions || [],
  }
  const retryPayments = paymentState.retry

  type RefetchPaymentOpts = {
    refetchLeadCardFirst?: boolean
    refreshStatuses?: boolean
    delayedPaymentRetry?: boolean
  }

  const handleRefetchPaymentData = useCallback(
    async (opts: RefetchPaymentOpts = {}) => {
      const {
        refetchLeadCardFirst = false,
        refreshStatuses: doStatuses = true,
        delayedPaymentRetry: doDelayed = true,
      } = opts

      if (refetchLeadCardFirst) {
        const funnelId =
          selectedFunnelId != null && Number(selectedFunnelId) > 0
            ? selectedFunnelId
            : initialFunnelIdForRefetch
        await handleRefetchLeadInModal(funnelId)
        retryPayments()
        handleRefetchLeadsOnClose()
        refetchExcludedIds()
        return
      }

      if (doStatuses) {
        refreshStatuses()
      }
      retryPayments()
      if (doDelayed) {
        setTimeout(() => retryPayments(), 700)
      }
      handleRefetchLeadsOnClose()
      refetchExcludedIds()
    },
    [
      handleRefetchLeadInModal,
      handleRefetchLeadsOnClose,
      initialFunnelIdForRefetch,
      refetchExcludedIds,
      refreshStatuses,
      retryPayments,
      selectedFunnelId,
    ],
  )

  /** Полная синхронизация карточки заявки + оплаты (статус по воронке и т.д. приходят с card / card-funnel). */
  const handleManualRefresh = () => {
    void handleRefetchPaymentData({ refetchLeadCardFirst: true })
  }

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <div className={classes.cardTitleCol}>
          <p className={classes.cardEyebrow}>Покупки</p>
          <p className={classes.cardTitle}>Курсы / Классы / Траектории</p>
        </div>
        <div className={classes.cardHeaderActions}>
          {hasLinkedAccount ? (
            <Button
              variant="action"
              color="gray"
              onClick={handleManualRefresh}
              disabled={paymentState.loading || isRefetchingLeadCard}
              leftContent={
                paymentState.loading || isRefetchingLeadCard ? (
                  <LoadingOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                ) : (
                  <ReloadOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                )
              }
            >
              Обновить
            </Button>
          ) : null}
          {access.canAddPurchases && hasFunnel ? (
            <Button
              variant="action"
              color="secondary"
              onClick={() => setStateModal({ open: true, type: 'add' })}
              leftContent={
                <PlusCircleFilled
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
            >
              Добавить покупку
            </Button>
          ) : null}
        </div>
      </div>
      {!hasFunnel && access.canAddPurchases ? (
        <p className={classes.createHint}>
          Без выбранной воронки создание покупок недоступно.
        </p>
      ) : null}
      <div className={classes.cardContent}>
        {!!lead.id ? (
          hasLinkedAccount ? (
            <CourseClassList
              userId={Number(lead.user_id)}
              loading={paymentState.loading}
              paymentData={paymentData}
              refetchPaymentData={() =>
                void handleRefetchPaymentData({ refetchLeadCardFirst: true })
              }
            />
          ) : (
            <Empty
              className={classes.empty}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                hasFunnel
                  ? 'Нет привязанного личного кабинета — покупки недоступны'
                  : 'Для лида не выбрана воронка'
              }
            />
          )
        ) : (
          <Empty
            className={classes.empty}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={'Лид не найден'}
          />
        )}
      </div>

      {/* Modals */}
      {hasFunnel ? (
        <SetCourseClassDirectionModal
          lead={lead}
          currentLeadFunnelId={currentLeadFunnelId}
          userId={lead?.user_id ?? null}
          excludedClassIds={excludedClassIds}
          excludedDirectionIds={excludedDirectionIds}
          open={stateModal.open && stateModal.type === 'add'}
          onClose={() => setStateModal({ open: false })}
          onSuccess={() => {
            void handleRefetchPaymentData({
              refetchLeadCardFirst: true,
            }).finally(() => {
              onKanbanCardSync?.()
              setStateModal({ open: false })
            })
          }}
        />
      ) : null}
    </div>
  )
}

export default PurchasesBlock
